import { Button, ClearableInput, Dialog } from '@rio-cloud/rio-uikit';
import { type FormEvent, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { updateOccurrenceDispatch } from '../../../../../../../api/occurrence';
import type { Occurrence, OccurrenceDispatch } from '../../../../../../../models/occurrence';
import ErrorDialog from '../../../../../../dialogs/ErrorDialog';

export type EditableDispatchField = 'protocol' | 'assistedCustomer';

interface EditDispatchFieldDialogProps {
    field: EditableDispatchField;
    show: boolean;
    onClose: () => void;
    occurrence: Occurrence | null;
    occurrenceDispatch: OccurrenceDispatch;
    onSaved: (value: string) => void;
}

const EditDispatchFieldDialog = (props: EditDispatchFieldDialogProps) => {
    const { field } = props;
    const intl = useIntl();
    const t = (key: string) => intl.formatMessage({ id: `intl-msg:dispatch.${field}.${key}` });
    const formId = `edit-dispatch-${field}-form`;
    const inputId = `edit-dispatch-${field}-input`;
    const currentValue = props.occurrenceDispatch[field] ?? '';

    const [value, setValue] = useState(currentValue);
    const [loading, setLoading] = useState(false);
    const [showErrorDialog, setShowErrorDialog] = useState(false);

    useEffect(() => {
        if (props.show) {
            setValue(currentValue);
        }
    }, [props.show, currentValue]);

    const trimmedValue = value.trim();
    const canSave = !loading && trimmedValue !== '' && trimmedValue !== currentValue;

    const handleClose = () => {
        if (!loading) {
            props.onClose();
        }
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (!canSave) {
            return;
        }

        if (!props.occurrence || !props.occurrenceDispatch.id) {
            setShowErrorDialog(true);
            return;
        }

        setLoading(true);
        const updated = await updateOccurrenceDispatch(props.occurrence.id, {
            ...props.occurrenceDispatch,
            [field]: trimmedValue,
        });
        setLoading(false);

        if (updated) {
            props.onSaved(trimmedValue);
            props.onClose();
        } else {
            setShowErrorDialog(true);
        }
    };

    return (
        <>
            <Dialog show={props.show} onClose={handleClose} showCloseButton bsSize={Dialog.SIZE_SM}>
                <>
                    <Dialog.Title title={t('edit')} />
                    <Dialog.Body>
                        <form
                            id={formId}
                            onSubmit={handleSubmit}
                            className='form-group form-group-lg w-full margin-bottom-0'
                        >
                            <label htmlFor={inputId}>{t('label')}</label>
                            <ClearableInput
                                id={inputId}
                                value={value}
                                placeholder={t('placeholder')}
                                onChange={newValue => setValue(newValue)}
                            />
                        </form>
                    </Dialog.Body>
                    <Dialog.Footer>
                        <div className='flex items-center justify-end gap-6'>
                            <Button bsStyle={Button.DEFAULT} onClick={handleClose} disabled={loading}>
                                {t('cancel')}
                            </Button>
                            <Button
                                bsStyle={Button.PRIMARY}
                                type='submit'
                                form={formId}
                                className={loading ? 'btn-loading' : undefined}
                                disabled={!canSave}
                            >
                                {t('save')}
                            </Button>
                        </div>
                    </Dialog.Footer>
                </>
            </Dialog>

            <ErrorDialog
                show={showErrorDialog}
                onClose={() => setShowErrorDialog(false)}
                title={t('error.title')}
                body={t('error.body')}
            />
        </>
    );
};

export default EditDispatchFieldDialog;
